// 🆕 Modelo para entradas de stock por lote en listado GET /locations (solo se muestra el primer lote)
export interface ProductTeam {
  id: number;
  name: string;
}

export interface LocationStockBatchEntry {
  /** La API puede enviar null; no usar String() directo sobre esto en UI */
  batch_id?: number | null;
  product_id?: number;
  quantity?: number;
  product?: Product;
  batch?: {
    id?: number;
    batch_number?: string | null;
    expiry_date?: string | null;
  };
}

const uselessBatchString = (s: string) => {
  const t = s.trim();
  return t === '' || t.toLowerCase() === 'null' || t.toLowerCase() === 'undefined';
};

/** Texto del badge de lote, o null si no hay datos mostrables (evita el literal "null" en pantalla). */
export function getDisplayableStockBatchLabel(batch: LocationStockBatchEntry): string | null {
  const rawNum = batch.batch?.batch_number;
  if (rawNum != null && rawNum !== '') {
    const s = String(rawNum).trim();
    if (!uselessBatchString(s)) return s;
  }
  const id = batch.batch_id;
  if (id == null) return null;
  if (typeof id === 'number' && Number.isFinite(id)) return String(id);
  if (typeof id === 'string') {
    const t = id.trim();
    if (uselessBatchString(t)) return null;
    const n = Number(t);
    if (!Number.isNaN(n) && Number.isFinite(n)) return String(n);
  }
  return null;
}

/** Primer lote de la ubicación solo si tiene etiqueta mostrable. */
export function getPrimaryDisplayableStockBatch(location: Location): LocationStockBatchEntry | undefined {
  const first = location.stock_batches?.[0];
  if (!first || getDisplayableStockBatchLabel(first) == null) return undefined;
  return first;
}

export interface Location {
  id: number;
  team_id: number;
  code: string;
  type_id: number;
  type: LocationType; // 🆕 Objeto completo anidado
  product_id: number;
  product: Product; // 🆕 Objeto completo anidado
  is_locked: boolean;
  deleted_at?: string | null; // 🆕 Para soft deletes
  stock: number; // 🆕 Stock actual
  /** Por ubicación típicamente un solo lote; el listado usa el primero */
  stock_batches?: LocationStockBatchEntry[];
  created_at?: string; // Mantener por si viene después
  updated_at?: string; // Mantener por si viene después
  rack_id?: number | null;
  shelf?: number | null;
  slot?: number | null;
}

// 🆕 DTOs separados para requests (sin objetos anidados)
export interface CreateLocationRequest {
  code: string;
  type_id: number;
  rack_id?: number | null;
  shelf?: number | null;
  slot?: number | null;
}

export interface UpdateLocationRequest {
  code: string;
  type_id: number;
  product_id: number;
  is_locked: boolean;
  rack_id?: number | null;
  shelf?: number | null;
  slot?: number | null;
}

// Filtros para búsqueda de locations
export interface LocationFilters {
  searchTerm?: string;
  productId?: number;
  typeId?: number;
  teamId?: number; // 🆕 Filtrar por equipo
  // 🎯 Campos específicos procesados desde filterConditions
  type_ids?: string | string[]; // Para múltiples tipos
  product_ids?: string | string[]; // Para múltiples productos
  product_team_ids?: string; // Filtrar por teams propietarios del producto (ej: "123,12,151")
  // 🎯 Campos para sorting
  sortBy?: string; // Campo por el que ordenar
  sortDirection?: 'asc' | 'desc'; // Dirección del ordenamiento
  page?: number;
  limit?: number;
}

// 🆕 Response paginada de la API
export interface LocationsApiResponse {
  data: Location[];
  meta: {
    currentPage: number;
    lastPage: number;
    perPage: number;
    total: number;
  };
}

// 🆕 Para useInfiniteQuery
export interface LocationPage {
  locations: Location[];
  nextPage?: number;
  hasMore: boolean;
  total: number; // 🎯 Total de resultados del API
}

// 🆕 Modelo completo para Product
export interface Product {
  id: number;
  name: string;
  image_hash?: string; // 🎯 Campo principal para imágenes
  image?: string; // 🎯 Campo fallback por compatibilidad
  code?: string; // 🆕 Código de producto
  team?: ProductTeam | null;
}

// 🆕 Modelo completo para LocationType
export interface LocationType {
  id: number;
  name: string;
}
